import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";

import { ProtectedRoute } from "./components/ProtectedRoute";
import { AppLayout } from "./components/AppLayout";
import { PageLoader } from "./components/PageLoader";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Lazy load all pages for code splitting
const Legal = lazy(() => import("./pages/Legal"));
const Landing = lazy(() => import("./pages/Landing"));
const Auth = lazy(() => import("./pages/Auth"));
const EmailConfirmation = lazy(() => import("./pages/EmailConfirmation"));
const ConfirmTrucks = lazy(() => import("./pages/ConfirmTrucks"));
const SupplierConfirmation = lazy(() => import("./pages/SupplierConfirmation"));
const ConfirmationSuccess = lazy(() => import("./pages/ConfirmationSuccess"));
const SupabaseConfig = lazy(() => import("./pages/SupabaseConfig"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CompanyDashboard = lazy(() => import("./pages/CompanyDashboard"));
const TeamManagement = lazy(() => import("./pages/TeamManagement"));
const Sites = lazy(() => import("./pages/Sites"));
const SiteDetailLayout = lazy(() => import("./components/site-detail/SiteDetailLayout"));
const CommandeGlobaleFormules = lazy(() => import("./pages/CommandeGlobaleFormules"));
const CreateCommande = lazy(() => import("./pages/CreateCommande"));
const Reports = lazy(() => import("./pages/Reports"));
const Fournisseurs = lazy(() => import("./pages/Fournisseurs"));
const Commandes = lazy(() => import("./pages/Commandes"));
const Documentation = lazy(() => import("./pages/Documentation"));
const Help = lazy(() => import("./pages/Help"));
const SiteHelp = lazy(() => import("./pages/site/Help"));
const Settings = lazy(() => import("./pages/Settings"));
const ProjectSupervisor = lazy(() => import("./pages/ProjectSupervisor"));
const Site = lazy(() => import("./pages/Site"));
const IncidentReport = lazy(() => import("./pages/IncidentReport").then(m => ({
  default: m.IncidentReport
})));
const Deliveries = lazy(() => import("./pages/Deliveries"));
const Progress = lazy(() => import("./pages/Progress"));
const SiteCalendar = lazy(() => import("./pages/SiteCalendar"));
const Notifications = lazy(() => import("./pages/Notifications"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Site sub-pages
const SiteDashboard = lazy(() => import("./pages/site/SiteDashboard"));
const SiteCommandeGlobale = lazy(() => import("./pages/site/SiteCommandeGlobale"));
const SitePrevisionnel = lazy(() => import("./pages/site/SitePrevisionnel"));
const SitePlanningAnalysis = lazy(() => import("./pages/site/SitePlanningAnalysis"));
const AIAgent = lazy(() => import("./pages/site/AIAgent"));

const OrderDetailPage = lazy(() => import("./pages/site/OrderDetailPage"));
const SiteBudget = lazy(() => import("./pages/site/SiteBudget"));
const SiteDepenses = lazy(() => import("./pages/site/SiteDepenses"));
const SiteIncidents = lazy(() => import("./pages/site/SiteIncidents"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));
const DeletedItems = lazy(() => import("./pages/DeletedItems"));
const OCRAnalytics = lazy(() => import("./pages/OCRAnalytics"));
const RealtimeTest = lazy(() => import("./pages/RealtimeTest"));
const PerformanceDashboard = lazy(() => import("./pages/PerformanceDashboard"));
const DesignSystem = lazy(() => import("./pages/DesignSystem"));
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { CookieConsent } from "./components/CookieConsent";
import { BackgroundSyncProvider } from "./components/BackgroundSyncProvider";
import { GlobalAgentProvider } from "./contexts/GlobalAgentContext";
import { GlobalAgentOverlay } from "./components/GlobalAgentOverlay";
import { useDeepLinks } from "./hooks/useDeepLinks";
import { useStatusBar } from "./hooks/useStatusBar";
// Push notifications disabled until Firebase is configured (crashes without google-services.json)
// import { useNativePush } from "./hooks/useNativePush";

/** Activates native Capacitor plugins inside BrowserRouter context. */
function NativePluginHandler() {
  useDeepLinks();
  useStatusBar();
  // useNativePush(); // Enable after adding google-services.json (see docs/20-mobile-signing.md)
  return null;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,        // 2 minutes - réduire requêtes réseau
      gcTime: 15 * 60 * 1000,          // 15 minutes - garder en cache plus longtemps
      refetchOnWindowFocus: false,     // Désactivé pour éviter refetch intempestifs
      refetchOnMount: false,           // Utiliser le cache quand disponible
      retry: 1,                        // Limiter les retries
    },
  },
});

/** Redirects legacy concrete pour / commande URLs to /site/orders/:id. */
function RedirectSitePour() {
  const { id, commandeId } = useParams();
  const pourId = id ?? commandeId ?? "";
  return <Navigate to={`/site/orders/${pourId}`} replace />;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BackgroundSyncProvider>
        <TooltipProvider skipDelayDuration={0}>
          <Toaster />
          <PWAInstallPrompt />
          <GlobalAgentProvider>
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
            <NativePluginHandler />
            <GlobalAgentOverlay />
            <Routes>
              {/* Public routes - with individual Suspense boundaries */}
              <Route path="/" element={<Suspense fallback={<PageLoader />}><Landing /></Suspense>} />
              <Route path="/auth" element={<Suspense fallback={<PageLoader />}><Auth /></Suspense>} />
              <Route path="/legal" element={<Suspense fallback={<PageLoader />}><Legal /></Suspense>} />
              <Route path="/confirm" element={<Suspense fallback={<PageLoader />}><EmailConfirmation /></Suspense>} />
              <Route path="/confirm-trucks" element={<Suspense fallback={<PageLoader />}><ConfirmTrucks /></Suspense>} />
              <Route path="/supplier-confirmation" element={<Suspense fallback={<PageLoader />}><SupplierConfirmation /></Suspense>} />
              <Route path="/confirmation-success" element={<Suspense fallback={<PageLoader />}><ConfirmationSuccess /></Suspense>} />
              <Route path="/supabase-config" element={<Suspense fallback={<PageLoader />}><SupabaseConfig /></Suspense>} />
              <Route path="/onboarding" element={
                <Suspense fallback={<PageLoader />}>
                  <ProtectedRoute requiredRole="company_director">
                    <Onboarding />
                  </ProtectedRoute>
                </Suspense>
              } />
              
              {/* All authenticated routes use unified AppLayout - Suspense is inside AppLayout */}
              <Route element={<AppLayout />}>
                {/* Dashboard - company_director and project_supervisor */}
                <Route path="/dashboard" element={<ProtectedRoute requiredRole={['company_director', 'project_supervisor']}>
                    <Dashboard />
                  </ProtectedRoute>} />
                <Route path="/team" element={<ProtectedRoute requiredRole={['company_director', 'project_supervisor']}>
                    <TeamManagement />
                  </ProtectedRoute>} />
                <Route path="/sites" element={<ProtectedRoute requiredRole={['company_director', 'project_supervisor']}>
                    <Sites />
                  </ProtectedRoute>} />
                <Route path="/sites/:id" element={<ProtectedRoute requiredRole={['company_director', 'project_supervisor']}><SiteDetailLayout /></ProtectedRoute>}>
                  <Route index element={<SiteDashboard />} />
                  <Route path="global-concrete-order" element={<SiteCommandeGlobale />} />
                  <Route path="previsionnel" element={<SitePrevisionnel />} />
                  <Route path="budget" element={<SiteBudget />} />
                  <Route path="planning" element={<SitePlanningAnalysis />} />
                  <Route path="costs" element={<SiteDepenses />} />
                  <Route path="incidents" element={<SiteIncidents />} />
                </Route>
                <Route path="/sites/:siteId/global-concrete-order" element={<ProtectedRoute requiredRole="company_director">
                    <CommandeGlobaleFormules />
                  </ProtectedRoute>} />
                <Route path="/commandes/new" element={<ProtectedRoute requiredRole="company_director">
                    <CreateCommande />
                  </ProtectedRoute>} />
                <Route path="/reports" element={<ProtectedRoute requiredRole="company_director">
                    <Reports />
                  </ProtectedRoute>} />
                <Route path="/fournisseurs" element={<ProtectedRoute requiredRole={['company_director', 'project_supervisor']}>
                    <Fournisseurs />
                  </ProtectedRoute>} />
                <Route path="/documentation" element={<ProtectedRoute requiredRole={['company_director', 'project_supervisor']}><Documentation /></ProtectedRoute>} />
                <Route path="/design-system" element={<ProtectedRoute><DesignSystem /></ProtectedRoute>} />
                <Route path="/help" element={<ProtectedRoute requiredRole={['company_director', 'project_supervisor']}><Help /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute requiredRole={['company_director', 'project_supervisor']}><Settings /></ProtectedRoute>} />
                <Route path="/settings/ocr-analytics" element={<ProtectedRoute requiredRole={['company_director', 'project_supervisor']}>
                    <OCRAnalytics />
                  </ProtectedRoute>} />
                <Route path="/profile" element={<Navigate to="/settings" replace />} />
                <Route path="/audit-logs" element={<ProtectedRoute requiredRole={['company_director', 'project_supervisor']}>
                    <AuditLogs />
                  </ProtectedRoute>} />
                <Route path="/deleted-items" element={<ProtectedRoute requiredRole={['company_director', 'project_supervisor']}>
                    <DeletedItems />
                  </ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute requiredRole={['company_director', 'project_supervisor']}>
                    <Notifications />
                  </ProtectedRoute>} />
                
                {/* Project supervisor routes */}
                <Route path="/conducteur" element={<ProtectedRoute requiredRole="project_supervisor">
                    <ProjectSupervisor />
                  </ProtectedRoute>} />
                <Route path="/conducteur/documentation" element={<ProtectedRoute requiredRole="project_supervisor"><Documentation /></ProtectedRoute>} />
                <Route path="/conducteur/help" element={<ProtectedRoute requiredRole="project_supervisor"><Help /></ProtectedRoute>} />
                <Route path="/conducteur/settings" element={<ProtectedRoute requiredRole="project_supervisor"><Settings /></ProtectedRoute>} />
                <Route path="/conducteur/profile" element={<Navigate to="/conducteur/settings" replace />} />
                
                {/* Site manager routes */}
                <Route path="/site" element={<ProtectedRoute requiredRole="site_manager">
                    <Site />
                  </ProtectedRoute>} />
                <Route path="/site/incident" element={<ProtectedRoute requiredRole="site_manager">
                    <IncidentReport />
                  </ProtectedRoute>} />
                <Route path="/site/history" element={<ProtectedRoute requiredRole="site_manager">
                    <Deliveries />
                  </ProtectedRoute>} />
                <Route path="/site/notifications" element={<ProtectedRoute requiredRole="site_manager">
                    <Notifications />
                  </ProtectedRoute>} />
                <Route path="/site/settings" element={<ProtectedRoute requiredRole="site_manager"><Settings /></ProtectedRoute>} />
                <Route path="/site/profile" element={<Navigate to="/site/settings" replace />} />
                <Route path="/site/orders/:id" element={<ProtectedRoute requiredRole="site_manager">
                    <OrderDetailPage />
                  </ProtectedRoute>} />
                <Route path="/site/concrete_pours/:id" element={<ProtectedRoute requiredRole="site_manager">
                    <RedirectSitePour />
                  </ProtectedRoute>} />
                <Route path="/site/commande/:commandeId" element={<ProtectedRoute requiredRole="site_manager">
                    <RedirectSitePour />
                  </ProtectedRoute>} />
                <Route path="/site/progress" element={<ProtectedRoute requiredRole="site_manager">
                    <Progress />
                  </ProtectedRoute>} />
                <Route path="/site/calendar" element={<ProtectedRoute requiredRole="site_manager">
                    <SiteCalendar />
                  </ProtectedRoute>} />
                <Route path="/site/planning" element={<ProtectedRoute requiredRole="site_manager">
                    <SitePlanningAnalysis />
                  </ProtectedRoute>} />
                <Route path="/site/help" element={<ProtectedRoute requiredRole="site_manager">
                    <SiteHelp />
                  </ProtectedRoute>} />
                <Route path="/site/documentation" element={<ProtectedRoute requiredRole="site_manager">
                    <Documentation />
                  </ProtectedRoute>} />
                <Route path="/site/agent" element={<ProtectedRoute requiredRole="site_manager">
                    <Site />
                  </ProtectedRoute>} />
              </Route>
              
              {/* Utility routes - with individual Suspense */}
              <Route path="/realtime-test" element={<Suspense fallback={<PageLoader />}><RealtimeTest /></Suspense>} />
              <Route path="/performance" element={<Suspense fallback={<PageLoader />}><PerformanceDashboard /></Suspense>} />
              
              {/* Catch-all */}
              <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
            </Routes>
            <CookieConsent />
            </BrowserRouter>
          </GlobalAgentProvider>
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-right" />}
        </TooltipProvider>
      </BackgroundSyncProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
